import { useEffect, useState } from 'react';
import { sessionStats, summarySentence, type SessionStats } from '../../core/stats';
import { db, type SessionRecord } from '../../engine/db';
import { TrendChart } from '../components/charts';
import { fmtDate, fmtPct, fmtTime, PageHead } from '../components/bits';
import { Icon } from '../components/Icon';

interface Row {
  session: SessionRecord;
  stats: SessionStats;
}

export function Progress() {
  const [rows, setRows] = useState<Row[] | null>(null);
  useEffect(() => {
    (async () => {
      const out: Row[] = [];
      for (const s of await db.listSessions()) {
        const attempts = await db.attemptsForSession(s.id);
        if (attempts.length) out.push({ session: s, stats: sessionStats(attempts.map((a) => a.analysis), s.params) });
      }
      setRows(out);
    })();
  }, []);

  if (!rows) return <div className="page"><div className="spinner" /></div>;
  const practice = rows.filter((r) => r.session.mode === 'practice' && r.stats.assessed >= 3).reverse();
  const label = (r: Row) => new Date(r.session.startedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  const totals = rows.reduce((acc, r) => ({ attempts: acc.attempts + r.stats.total, clean: acc.clean + r.stats.counts.clean }), { attempts: 0, clean: 0 });

  return (
    <div className="page stack lg">
      <PageHead title="Progress" />
      {rows.length === 0 ? (
        <div className="empty">
          <Icon name="chart" size={40} />
          <p>Your sessions will appear here.</p>
        </div>
      ) : (
        <>
          <div className="tiles three">
            <div className="tile">
              <div className="label">Sessions</div>
              <div className="value">{rows.length}</div>
            </div>
            <div className="tile">
              <div className="label">Pop-ups</div>
              <div className="value">{totals.attempts}</div>
            </div>
            <div className="tile">
              <div className="label">Clean landings</div>
              <div className="value">{totals.clean}</div>
            </div>
          </div>

          {practice.length >= 2 ? (
            <div className="stack">
              <div className="card stack sm">
                <h3>Clean landing rate</h3>
                <p className="small muted">Share of assessed attempts that landed on target with no shuffle.</p>
                <TrendChart ariaLabel="Clean landing rate per session" data={practice.map((r) => ({ label: label(r), value: r.stats.cleanRate }))} format={(v) => `${Math.round(v * 100)}%`} domain={[0, 1]} />
              </div>
              <div className="card stack sm">
                <h3>Front-foot scatter</h3>
                <p className="small muted">How spread out your front-foot landings were. Lower means more consistent.</p>
                <TrendChart
                  ariaLabel="Front-foot landing scatter per session, centimetres"
                  color="var(--front)"
                  data={practice.map((r) => ({ label: label(r), value: r.stats.spread?.front ?? null }))}
                  format={(v) => `${Math.round(v)} cm`}
                />
              </div>
              <div className="card stack sm">
                <h3>Pop-up time on clean attempts</h3>
                <p className="small muted">Median time from first movement to both feet planted. Speed only counts when the landing is clean.</p>
                <TrendChart
                  ariaLabel="Median clean pop-up time per session, seconds"
                  data={practice.map((r) => ({ label: label(r), value: r.stats.medianCleanPopupMs === null ? null : r.stats.medianCleanPopupMs / 1000 }))}
                  format={(v) => `${v.toFixed(1)} s`}
                />
              </div>
            </div>
          ) : (
            <p className="muted small">Trends appear after two practice sessions with at least three assessed attempts each.</p>
          )}

          <div>
            <div className="section-title">All sessions</div>
            <div className="card flush list">
              {rows.map(({ session, stats }) => (
                <a key={session.id} className="list-item" href={`#/session/${session.id}`}>
                  <div className="grow">
                    <div style={{ fontWeight: 600 }}>
                      {fmtDate(session.startedAt)} <span className="faint">· {fmtTime(session.startedAt)}</span>
                      {session.mode === 'experiment' && <span className="faint"> · validation</span>}
                      {session.source === 'demo' && <span className="faint"> · demo</span>}
                    </div>
                    <div className="small muted">{summarySentence(stats)}</div>
                  </div>
                  <div className="num" style={{ fontWeight: 650 }}>{fmtPct(stats.cleanRate)}</div>
                  <Icon name="chevron" size={18} className="faint" />
                </a>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
